import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@cloudscape-design/collection-hooks';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import Pagination from '@cloudscape-design/components/pagination';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table, { type TableProps } from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import {
  ALL_TENANT_REGIONS,
  currencyForCountry,
  EU_COUNTRIES,
  type MarketConfig,
  type Tenant,
  type TenantRegion,
} from '@etiquetachef/types';
import {
  subscribeMarketConfigs,
  type MarketConfigListResult,
} from '@/lib/firestoreTenantDetail';
import { subscribeTenants } from '@/lib/firestoreTenants';
import { setMarketConfigCallable } from '@/lib/callables';
import { toast } from '@/lib/notifications';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { CountryEditDialog } from './CountryEditDialog';
import { RegionsSwitch } from './RegionsSwitch';
import { regionLabelKey } from './regionLabels';
import {
  CONTINENT_KEYS,
  countryDisplayName,
  flagEmoji,
  type ContinentKey,
} from './worldCountries';
import { buildCountryRows, isActive, type CountryRow } from './countryRows';
import { countryPopulation } from './countryPopulation';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

type StatusFilter = 'all' | 'active' | 'inactive' | 'unconfigured';

/** Linhas por página: um continente inteiro cabe numa página (o maior, a África, tem 57). */
const PAGE_SIZE = 100;

/**
 * Aba Países de `/countries`: os 225 locais da Google Play (+ todo país com
 * doc), configurados ou não, com CRUD — editar catálogo, ativar/desativar
 * (soft — país nunca é deletado) e configurar em massa.
 *
 * Origem: sem issue — commit a0529e97c, 2026-06-11 14:58 +0200 (Sprint 36 Fase 2c).
 *
 * Por quê: o universo de países do sistema é a lista da Play — o sistema não
 * exclui país, quem exclui é o console da Play (owner, 2026-10-13, #954). Até
 * a #957 a tabela mostrava só quem tinha doc e o resto ficava escondido num
 * picker com uma lista própria de 196 países (ONU), divergente da Play.
 *
 * Contrato: assina `config/markets/items` (`subscribeMarketConfigs`) e os
 * tenants (`subscribeTenants`, só para a contagem ativos/inativos por país).
 * Toda escrita vai pela callable `setMarketConfig`, com `reason` obrigatório:
 * ativar/desativar reenvia o roteamento atual (ou zera PSP/entidade quando o
 * país não vende); editar (ou "Configurar", na linha sem doc) abre
 * `CountryEditDialog`; "Configurar selecionados"/"Configurar UE" criam, depois
 * de confirmação, uma entrada `saleable:false, active:true` por país.
 *
 * Usado por: `routes/CountriesPage.tsx` (aba Países de `/countries`).
 *
 * Arquitetura: `match /config/markets/items/{country}` nega escrita do
 * cliente; os guards de roteamento PSP/fiscal (Asaas só no BR, Stripe
 * universal) moram em `apps/functions/src/admin/setMarketConfig.ts`. Desativar
 * país com qualquer tenant cadastrado é recusado no servidor (`country_has_tenants`).
 * Linha sem doc não existe no backend — "Não configurado" é só tela; o
 * servidor cria o doc no primeiro `setMarketConfig` (merge).
 * Spec: `docs/screens/countries-admin.md`.
 *
 * Dados: `config/markets/items/{ISO-2}` — `MarketConfigSchema`
 * (`packages/types/src/schemas.ts`); `active` ausente = ativo.
 *
 * Dicas: a contagem de tenants vem de uma lista com teto
 * (`TENANTS_LIST_LIMIT`) — é referência, não fonte da verdade; a decisão de
 * bloquear desativação é do servidor. Configurar em massa grava em produção
 * (um callable por país, em série) — por isso pede confirmação.
 *
 * Issues: #954 (lista da Play), #957 (os 225 na tabela), #756 (PSP no editor),
 * #1078 (região do país, F2 da #960).
 *
 * Histórico:
 *  - 2026-10-13 11:25 +0200 — (#957) tabela com os 225 locais da Play e
 *    linhas "Não configurado"; filtros de continente e texto, paginação;
 *    "Adicionar país" (picker de 196) vira "Configurar selecionados"/"Configurar UE".
 *  - 2026-10-13 12:40 +0200 — (#957, auditoria A1) trocar filtro zera a seleção.
 *  - 2026-10-13 13:10 +0200 — (#958) sem `seller_entity_id` no payload: o
 *    país não escolhe entidade (a PJ sai do PSP), e o servidor o ignora.
 *  - 2026-10-25 15:00 +0100 — (#1021) a recusa do ativar/desativar sai num
 *    `Alert` dentro do confirm, que fica aberto (o toast ficava atrás do overlay, #174).
 *  - 2026-10-29 10:20 +0100 — (#1078, F2 da #960) coluna e filtro Região (override ou padrão do
 *    grupo) e a chave `config/regions` no topo (`RegionsSwitch`).
 */
export function CountriesTab(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [markets, setMarkets] = useState<MarketConfigListResult>({ kind: 'LOADING' });
  const [editing, setEditing] = useState<{ country: string; current: MarketConfig | null } | null>(
    null,
  );
  const [toggling, setToggling] = useState<MarketConfig | null>(null);
  const [busy, setBusy] = useState(false);
  // #1021 (2026-10-25 15:00 +0100): recusa do ativar/desativar, mostrada dentro do confirm.
  const [toggleError, setToggleError] = useState<string | null>(null);
  // Códigos a configurar em massa, aguardando a confirmação (null = fechado).
  const [configuring, setConfiguring] = useState<readonly string[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [continentFilter, setContinentFilter] = useState<ContinentKey | 'all'>('all');
  // #1078 (F2 da #960): filtro pela região efetiva (override ou padrão do grupo).
  const [regionFilter, setRegionFilter] = useState<TenantRegion | 'all'>('all');
  const [selected, setSelected] = useState<readonly CountryRow[]>([]);
  const [tenants, setTenants] = useState<readonly Tenant[]>([]);

  useEffect(() => subscribeMarketConfigs(setMarkets), []);
  useEffect(
    () =>
      subscribeTenants(
        { status: null },
        (r): void => { setTenants(r.tenants); },
        (e): void => { console.error('countries: falha ao assinar tenants', e); },
      ),
    [],
  );

  // Contagem de tenants por país (ativos vs inativos). DELETED é estado terminal
  // ("some de queries de operação") → não conta em nenhum dos dois. Inativos =
  // SUSPENDED_BILLING + SUSPENDED_ADMIN. Derivado de `subscribeTenants` (teto
  // TENANTS_LIST_LIMIT — referência informativa, não fonte da verdade).
  const tenantCounts = useMemo(() => {
    const map = new Map<string, { active: number; inactive: number }>();
    for (const tn of tenants) {
      const cc = tn.countryCode;
      if (!cc || tn.status === 'DELETED') continue;
      const entry = map.get(cc) ?? { active: 0, inactive: 0 };
      if (tn.status === 'ACTIVE') entry.active += 1;
      else entry.inactive += 1;
      map.set(cc, entry);
    }
    return map;
  }, [tenants]);

  const displayName = (row: CountryRow): string =>
    row.market?.name ?? countryDisplayName(row.country, i18n.language);

  const rows = useMemo(
    () =>
      buildCountryRows(markets.kind === 'READY' ? markets.markets : []).sort((a, b) =>
        countryDisplayName(a.country, i18n.language).localeCompare(
          countryDisplayName(b.country, i18n.language),
        ),
      ),
    [markets, i18n.language],
  );

  // Status e continente filtram antes do `useCollection`; o texto (nome ou
  // código) filtra dentro dele, para o `TextFilter` mostrar a contagem.
  const visible = useMemo(
    () =>
      rows.filter((r) => {
        if (continentFilter !== 'all' && r.continent !== continentFilter) return false;
        if (regionFilter !== 'all' && r.region !== regionFilter) return false;
        switch (statusFilter) {
          case 'all':
            return true;
          case 'unconfigured':
            return r.market === null;
          case 'active':
            return isActive(r);
          case 'inactive':
            return r.market !== null && !isActive(r);
        }
      }),
    [rows, statusFilter, continentFilter, regionFilter],
  );

  const populationSum = useMemo(
    () => selected.reduce((acc, r) => acc + (countryPopulation(r.country) ?? 0), 0),
    [selected],
  );
  const selectedUnconfigured = useMemo(
    () => selected.filter((r) => r.market === null).map((r) => r.country),
    [selected],
  );
  const euUnconfigured = useMemo(() => {
    const configured = new Set(rows.filter((r) => r.market !== null).map((r) => r.country));
    return EU_COUNTRIES.filter((c) => !configured.has(c));
  }, [rows]);

  // Ordenação client-side (clique no cabeçalho) sobre a lista já filtrada.
  // `sortingComparator` por coluna porque as células são derivadas (nome
  // localizado, bandeira, população, contagem de tenants) — não há campo cru
  // que sirva de `sortingField`.
  const {
    items: pageItems,
    collectionProps,
    filterProps,
    filteredItemsCount,
    paginationProps,
  } = useCollection(visible, {
    sorting: {},
    pagination: { pageSize: PAGE_SIZE },
    filtering: {
      filteringFunction: (row, text): boolean => {
        const q = text.trim().toLowerCase();
        if (q === '') return true;
        return (
          row.country.toLowerCase().includes(q) ||
          countryDisplayName(row.country, i18n.language).toLowerCase().includes(q) ||
          (row.market?.name ?? '').toLowerCase().includes(q)
        );
      },
    },
  });

  // Auditoria 2026-10-13 12:40 +0200 (#957, A1): a seleção do Cloudscape
  // sobrevive à troca de filtro, e "Configurar selecionados" gravaria em
  // produção países que já nem estão na tela. Filtro mudou → seleção zera.
  const filteringText = filterProps.filteringText;
  useEffect(() => {
    setSelected([]);
  }, [statusFilter, continentFilter, regionFilter, filteringText]);

  // Memoizado: o Cloudscape rastreia a coluna de ordenação ativa por IDENTIDADE
  // do `sortingComparator`. Recriar os comparators a cada render quebra o
  // toggle asc/desc (a coluna ativa "some"). Deps só mudam quando o conteúdo
  // muda (locale, contagem de empresas) — aí reordena com a nova função.
  const columnDefinitions = useMemo<readonly TableProps.ColumnDefinition<CountryRow>[]>(
    () => [
      {
        id: 'country',
        header: t('admin.countries.catalog.columns.country'),
        sortingComparator: (a, b): number =>
          countryDisplayName(a.country, i18n.language).localeCompare(
            countryDisplayName(b.country, i18n.language),
          ),
        cell: (r): React.ReactNode => (
          <SpaceBetween direction="horizontal" size="xs">
            <span style={{ fontSize: 18 }}>{flagEmoji(r.country)}</span>
            <Box variant="strong">
              {r.market?.name ?? countryDisplayName(r.country, i18n.language)}
            </Box>
            <Box color="text-status-inactive" fontSize="body-s">
              {r.country}
            </Box>
          </SpaceBetween>
        ),
      },
      {
        id: 'phone',
        header: t('admin.countries.catalog.columns.phone'),
        sortingComparator: (a, b): number =>
          (a.market?.phone_country_code ?? '').localeCompare(
            b.market?.phone_country_code ?? '',
            undefined,
            { numeric: true },
          ),
        cell: (r): React.ReactNode => r.market?.phone_country_code ?? '—',
      },
      {
        id: 'active',
        header: t('admin.countries.catalog.columns.active'),
        // Não configurado (-1) < inativo (0) < ativo (1).
        sortingComparator: (a, b): number => statusRank(a) - statusRank(b),
        cell: (r): React.ReactNode => {
          if (r.market === null) {
            return <Badge>{t('admin.countries.catalog.notConfigured')}</Badge>;
          }
          return isActive(r) ? (
            <Badge color="green">{t('admin.countries.catalog.on')}</Badge>
          ) : (
            <Badge color="grey">{t('admin.countries.catalog.off')}</Badge>
          );
        },
      },
      {
        id: 'otp',
        header: t('admin.countries.catalog.columns.otp'),
        sortingComparator: (a, b): number =>
          Number(a.market?.otp_enabled ?? false) - Number(b.market?.otp_enabled ?? false),
        cell: (r): React.ReactNode => (r.market?.otp_enabled ? '✓' : '—'),
      },
      {
        id: 'payments',
        header: t('admin.countries.catalog.columns.payments'),
        sortingComparator: (a, b): number =>
          Number(a.market?.saleable ?? false) - Number(b.market?.saleable ?? false),
        cell: (r): React.ReactNode =>
          r.market?.saleable ? (
            <Badge color="blue">{t('admin.countries.catalog.on')}</Badge>
          ) : (
            '—'
          ),
      },
      {
        id: 'psp',
        header: t('admin.countries.catalog.columns.psp'),
        sortingComparator: (a, b): number =>
          (a.market?.payment_provider ?? '').localeCompare(b.market?.payment_provider ?? ''),
        cell: (r): React.ReactNode => r.market?.payment_provider ?? '—',
      },
      {
        // #1078 (F2 da #960): onde nascem os tenants NOVOS do país com a chave ligada.
        id: 'region',
        header: t('admin.countries.catalog.columns.region'),
        sortingComparator: (a, b): number => a.region.localeCompare(b.region),
        cell: (r): React.ReactNode => (
          <SpaceBetween direction="horizontal" size="xxs">
            <Badge color={r.regionSource === 'override' ? 'blue' : 'grey'}>
              {t(regionLabelKey(r.region))}
            </Badge>
            <Box color="text-status-inactive" fontSize="body-s">
              {r.regionSource === 'override'
                ? t('admin.countries.catalog.region.override')
                : t('admin.countries.catalog.region.default')}
            </Box>
          </SpaceBetween>
        ),
      },
      {
        id: 'currency',
        header: t('admin.countries.catalog.columns.currency'),
        sortingComparator: (a, b): number =>
          (a.market?.currency ?? '').localeCompare(b.market?.currency ?? ''),
        cell: (r): React.ReactNode => r.market?.currency ?? '—',
      },
      {
        id: 'population',
        header: t('admin.countries.catalog.columns.population'),
        sortingComparator: (a, b): number =>
          (countryPopulation(a.country) ?? 0) - (countryPopulation(b.country) ?? 0),
        cell: (r): React.ReactNode => {
          const pop = countryPopulation(r.country);
          return pop == null ? '—' : new Intl.NumberFormat(i18n.language).format(pop);
        },
      },
      {
        id: 'companies',
        header: t('admin.countries.catalog.columns.companies'),
        sortingComparator: (a, b): number => {
          const ta = tenantCounts.get(a.country);
          const tb = tenantCounts.get(b.country);
          return (
            (ta?.active ?? 0) + (ta?.inactive ?? 0) - ((tb?.active ?? 0) + (tb?.inactive ?? 0))
          );
        },
        cell: (r): React.ReactNode => {
          const c = tenantCounts.get(r.country);
          return (
            <SpaceBetween direction="horizontal" size="xxs">
              <Badge color="green">{c?.active ?? 0}</Badge>
              <Badge color="grey">{c?.inactive ?? 0}</Badge>
            </SpaceBetween>
          );
        },
      },
      {
        id: 'actions',
        header: t('admin.countries.catalog.columns.actions'),
        cell: (r): React.ReactNode => {
          const m = r.market;
          if (m === null) {
            return (
              <Button
                variant="inline-link"
                onClick={(): void => { setEditing({ country: r.country, current: null }); }}
              >
                {t('admin.countries.catalog.configure')}
              </Button>
            );
          }
          return (
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="inline-link"
                onClick={(): void => { setEditing({ country: m.country, current: m }); }}
              >
                {t('admin.countries.catalog.edit')}
              </Button>
              <Button
                variant="inline-link"
                disabled={(m.active ?? true) && (tenantCounts.get(m.country)?.active ?? 0) > 0}
                onClick={(): void => {
                  setToggleError(null);
                  setToggling(m);
                }}
              >
                {(m.active ?? true)
                  ? t('admin.countries.catalog.deactivate')
                  : t('admin.countries.catalog.activate')}
              </Button>
            </SpaceBetween>
          );
        },
      },
    ],
    [t, i18n.language, tenantCounts],
  );

  /**
   * Inverte `active` do país em `toggling`, reenviando o roteamento atual.
   *
   * Origem: sem issue — commit a0529e97c, 2026-06-11 (aba Países, Sprint 36).
   *
   * Por quê o roteamento vai junto: `setMarketConfig` regrava a entrada
   * inteira; mandar só `active` apagaria o PSP e o fiscal do país vendável.
   * País não vendável vai com `payment_provider: null`.
   *
   * Usado por: o diálogo de confirmação "ativar/desativar" desta aba
   * (`apps/admin`, /countries); callable `setMarketConfig` (Functions).
   */
  async function toggleActive(): Promise<void> {
    if (!toggling) return;
    setBusy(true);
    setToggleError(null);
    try {
      const m = toggling;
      await setMarketConfigCallable({
        country: m.country,
        saleable: m.saleable,
        ...(m.saleable
          ? {
              payment_provider: m.payment_provider,
              fiscal_provider: m.fiscal_provider,
            }
          : { payment_provider: null }),
        active: !(m.active ?? true),
        reason: t('admin.countries.catalog.toggleActiveReason'),
      });
      toast.success(t('admin.countries.catalog.saved'));
      setToggling(null);
    } catch (err) {
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `toast.error`, que fica atrás do overlay do
      // confirm (mesmo achado do CRM, #174) — e o confirm não fecha no erro (ex.: `country_has_tenants`).
      setToggleError(err instanceof Error ? err.message : t('admin.countries.catalog.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Cria uma entrada bloqueada (`saleable:false, active:true`) por código em
   * `configuring`, um callable por vez; falha de um país não interrompe os
   * demais e o toast diz quantos entraram. Mesma carga do antigo
   * `AddCountriesDialog` (nome localizado, moeda de `currencyForCountry`,
   * `otp_enabled:false`, `tax_id_required:true`).
   */
  async function configureCountries(): Promise<void> {
    const codes = configuring ?? [];
    if (codes.length === 0) {
      setConfiguring(null);
      return;
    }
    setBusy(true);
    let ok = 0;
    try {
      for (const code of codes) {
        try {
          await setMarketConfigCallable({
            country: code,
            saleable: false,
            payment_provider: null,
            name: countryDisplayName(code, i18n.language),
            currency: currencyForCountry(code),
            active: true,
            otp_enabled: false,
            tax_id_required: true,
            reason: t('admin.countries.catalog.bulkAddReason'),
          });
          ok += 1;
        } catch (e) {
          console.error('configureCountry failed', code, e);
          // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
          reportNonFatal('admin_countries_tab_failed', {
            site: 'configureCountry failed',
            code: (e as { code?: string }).code ?? null,
            ...serializeError(e),
          });
        }
      }
      if (ok === codes.length) toast.success(t('admin.countries.catalog.bulkAdded', { count: ok }));
      else toast.error(t('admin.countries.catalog.bulkPartial', { ok, total: codes.length }));
      setConfiguring(null);
      setSelected([]);
    } finally {
      setBusy(false);
    }
  }

  if (markets.kind === 'LOADING') {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }
  if (markets.kind === 'ERROR') {
    return (
      <Box color="text-status-error" padding="l">
        {t('admin.countries.catalog.errorLoad')}
      </Box>
    );
  }

  const continentOptions = [
    { value: 'all', label: t('admin.countries.catalog.filter.allContinents') },
    ...CONTINENT_KEYS.map((c) => ({
      value: c,
      label: t(`admin.countries.catalog.continents.${c}`),
    })),
  ];

  const regionOptions = [
    { value: 'all', label: t('admin.countries.catalog.filter.allRegions') },
    ...ALL_TENANT_REGIONS.map((r) => ({ value: r, label: t(regionLabelKey(r)) })),
  ];

  return (
    <SpaceBetween size="l">
      <RegionsSwitch />
      <Table<CountryRow>
        {...collectionProps}
        items={pageItems}
        trackBy="country"
        variant="container"
        selectionType="multi"
        selectedItems={[...selected]}
        onSelectionChange={({ detail }): void => { setSelected(detail.selectedItems); }}
        ariaLabels={{
          selectionGroupLabel: t('admin.countries.catalog.columns.country'),
          allItemsSelectionLabel: (): string => t('admin.countries.catalog.filter.all'),
          itemSelectionLabel: (_data, r): string => displayName(r),
        }}
        footer={
          selected.length > 0 ? (
            <Box textAlign="right" variant="strong">
              {t('admin.countries.catalog.populationSum', {
                count: selected.length,
                value: new Intl.NumberFormat(i18n.language).format(populationSum),
              })}
            </Box>
          ) : undefined
        }
        header={
          <Header
            counter={`(${String(filteredItemsCount ?? visible.length)}/${String(rows.length)})`}
            description={t('admin.countries.catalog.tableDescription')}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  disabled={euUnconfigured.length === 0}
                  onClick={(): void => { setConfiguring(euUnconfigured); }}
                >
                  {t('admin.countries.catalog.configureEu', { count: euUnconfigured.length })}
                </Button>
                <Button
                  variant="primary"
                  disabled={selectedUnconfigured.length === 0}
                  onClick={(): void => { setConfiguring(selectedUnconfigured); }}
                >
                  {t('admin.countries.catalog.configureSelected', {
                    count: selectedUnconfigured.length,
                  })}
                </Button>
              </SpaceBetween>
            }
          >
            {t('admin.countries.catalog.tableTitle')}
          </Header>
        }
        filter={
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <TextFilter
              {...filterProps}
              filteringPlaceholder={t('admin.countries.catalog.filter.searchPlaceholder')}
              filteringAriaLabel={t('admin.countries.catalog.filter.searchPlaceholder')}
            />
            <Select
              selectedOption={
                continentOptions.find((o) => o.value === continentFilter) ?? continentOptions[0] ?? null
              }
              options={continentOptions}
              ariaLabel={t('admin.countries.catalog.filter.continentLabel')}
              onChange={({ detail }): void => {
                setContinentFilter((detail.selectedOption.value ?? 'all') as ContinentKey | 'all');
              }}
            />
            <Select
              selectedOption={
                regionOptions.find((o) => o.value === regionFilter) ?? regionOptions[0] ?? null
              }
              options={regionOptions}
              ariaLabel={t('admin.countries.catalog.filter.regionLabel')}
              onChange={({ detail }): void => {
                setRegionFilter((detail.selectedOption.value ?? 'all') as TenantRegion | 'all');
              }}
            />
            <SegmentedControl
              selectedId={statusFilter}
              label={t('admin.countries.catalog.filter.label')}
              options={[
                { id: 'all', text: t('admin.countries.catalog.filter.all') },
                { id: 'active', text: t('admin.countries.catalog.filter.active') },
                { id: 'inactive', text: t('admin.countries.catalog.filter.inactive') },
                { id: 'unconfigured', text: t('admin.countries.catalog.filter.unconfigured') },
              ]}
              onChange={({ detail }): void => {
                setStatusFilter(detail.selectedId as StatusFilter);
              }}
            />
          </SpaceBetween>
        }
        pagination={<Pagination {...paginationProps} />}
        empty={
          <Box textAlign="center" padding="l" color="inherit">
            {t('admin.countries.catalog.filter.noMatch')}
          </Box>
        }
        columnDefinitions={columnDefinitions}
      />

      <CountryEditDialog
        open={editing !== null}
        country={editing?.country ?? null}
        current={editing?.current ?? null}
        activeTenantCount={tenantCounts.get(editing?.country ?? '')?.active ?? 0}
        onClose={(): void => { setEditing(null); }}
      />

      <AdminConfirm
        visible={configuring !== null}
        title={t('admin.countries.catalog.configureConfirmTitle')}
        confirmLabel={t('admin.countries.catalog.confirmApply')}
        cancelLabel={t('admin.countries.catalog.cancel')}
        loading={busy}
        onConfirm={(): void => {
          void configureCountries();
        }}
        onDismiss={(): void => { setConfiguring(null); }}
      >
        <Box variant="p">
          {t('admin.countries.catalog.configureConfirmDescription', {
            count: configuring?.length ?? 0,
            countries: (configuring ?? []).join(', '),
          })}
        </Box>
      </AdminConfirm>

      <AdminConfirm
        visible={toggling !== null}
        title={
          (toggling?.active ?? true)
            ? t('admin.countries.catalog.deactivateTitle')
            : t('admin.countries.catalog.activateTitle')
        }
        confirmLabel={t('admin.countries.catalog.confirmApply')}
        cancelLabel={t('admin.countries.catalog.cancel')}
        loading={busy}
        onConfirm={(): void => {
          void toggleActive();
        }}
        onDismiss={(): void => {
          setToggling(null);
          setToggleError(null);
        }}
      >
        <SpaceBetween size="m">
          <Box variant="p">
            {t('admin.countries.catalog.toggleActiveDescription', { country: toggling?.country ?? '' })}
          </Box>
          {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do confirm, junto do footer (#174). */}
          {toggleError !== null && <Alert type="error">{toggleError}</Alert>}
        </SpaceBetween>
      </AdminConfirm>
    </SpaceBetween>
  );
}

/** Ordem da coluna Ativo: não configurado < inativo < ativo. */
function statusRank(row: CountryRow): number {
  if (row.market === null) return -1;
  return isActive(row) ? 1 : 0;
}
